import { CATEGORIES } from "../data/questionnaire";
import { PRESTART_CATEGORIES } from "../data/prestartQuestionnaire";

// ----- Existing-business mapping -----
const SERVICE_MAP = {
  cash_flow_gap: {
    label: "Cash Flow",
    services: [
      { title: "13-week cash flow forecasting", description: "Build a rolling 13-week cash flow forecast to bring visibility to the next three months." },
      { title: "Working capital review", description: "Review current working capital cycle and identify levers to release tied-up cash." },
      { title: "Debtor & creditor analysis", description: "Analyse outstanding debtors and creditor terms to improve cash conversion." },
    ],
    focus: "Cash flow visibility and short-term liquidity",
    challenge: "Limited visibility over cash flow and short-term liquidity",
  },
  debtor_issues: {
    label: "Debtor Management",
    services: [
      { title: "Debtor management process review", description: "Establish a structured collections rhythm and review credit policies." },
    ],
    focus: "Reducing days sales outstanding",
    challenge: "Customer payment delays affecting cash flow",
  },
  margin_unclear: {
    label: "Profitability",
    services: [
      { title: "Profitability deep dive", description: "Analyse profit by product, service line and customer segment." },
    ],
    focus: "Understanding where profit is being made and lost",
    challenge: "Limited visibility over where profit is genuinely being made",
  },
  pricing_uncertain: {
    label: "Pricing",
    services: [
      { title: "Pricing & profitability review", description: "Review current pricing approach against costs, market and target margins." },
      { title: "Margin analysis", description: "Quantify gross and contribution margins across the business." },
      { title: "Break-even analysis", description: "Identify the volume and price points needed to cover fixed and variable costs." },
    ],
    focus: "Pricing confidence and margin discipline",
    challenge: "Uncertainty around pricing strategy and margin protection",
  },
  funding_planned: {
    label: "Funding",
    services: [
      { title: "Funding readiness support", description: "Prepare the business and its financials to present to lenders or investors." },
      { title: "Financial forecast preparation", description: "Build 3-year financial forecasts suitable for funding applications." },
      { title: "Business plan support", description: "Develop a structured business plan to support funding conversations." },
    ],
    focus: "Funding readiness and lender presentation",
    challenge: "Upcoming capital or funding requirement",
  },
  systems_issues: {
    label: "Systems",
    services: [
      { title: "Financial systems review", description: "Audit current accounting and operational systems for gaps and inefficiencies." },
      { title: "Xero / accounting setup review", description: "Review chart of accounts, tracking categories and reporting setup." },
      { title: "KPI dashboard recommendations", description: "Recommend a lightweight KPI dashboard tailored to your business." },
    ],
    focus: "Reliable systems and meaningful dashboards",
    challenge: "Underutilised or disorganised financial systems",
  },
  reporting_gap: {
    label: "Reporting",
    services: [
      { title: "Management reporting framework", description: "Establish a monthly reporting pack that surfaces what actually matters." },
    ],
    focus: "Consistent, decision-ready reporting",
    challenge: "Infrequent or unclear financial reporting",
  },
  staffing_concerns: {
    label: "Team",
    services: [
      { title: "Team & capacity review", description: "Discuss current team structure and where capacity gaps may be limiting growth." },
    ],
    focus: "Team structure and capacity",
    challenge: "Staffing or team capacity concerns",
  },
  growth_planning: {
    label: "Growth",
    services: [
      { title: "Growth strategy session", description: "Facilitated session to define growth priorities for the next 1–3 years." },
    ],
    focus: "Clear growth direction",
    challenge: "Absence of a structured growth plan",
  },
  low_confidence: {
    label: "Confidence",
    services: [
      { title: "Financial understanding sessions", description: "Personalised sessions to help you confidently read and act on your numbers." },
      { title: "Business mentoring", description: "Ongoing mentoring with an EYB advisor as you navigate decisions." },
      { title: "Monthly advisory support", description: "Regular monthly check-ins to discuss performance and priorities." },
    ],
    focus: "Owner confidence and financial literacy",
    challenge: "Low confidence in interpreting business financials",
  },
  compliance_concern: {
    label: "Compliance",
    services: [
      { title: "Compliance health check", description: "Review of current tax, BAS and regulatory obligations to identify risks." },
    ],
    focus: "Compliance reassurance",
    challenge: "Open compliance or regulatory concerns",
  },
  planning_gap: {
    label: "Planning",
    services: [
      { title: "Business plan development", description: "Collaborative build of a working business plan you'll actually use." },
    ],
    focus: "Strategic planning",
    challenge: "No documented business plan in place",
  },
  mentoring_need: {
    label: "Mentoring",
    services: [
      { title: "Business mentoring engagement", description: "Ongoing mentoring relationship with an EYB advisor." },
    ],
    focus: "Trusted advisor relationship",
    challenge: "Lack of a trusted sounding board for business decisions",
  },
};

// ----- Pre-start mapping -----
const PRESTART_SERVICE_MAP = {
  concept_unclear: {
    label: "Concept",
    services: [
      { title: "Business concept clarification session", description: "Structured session to sharpen the core idea, customer problem and value proposition before committing time and money." },
    ],
    focus: "Sharpening the business concept",
    challenge: "Business idea still being defined",
  },
  market_unknown: {
    label: "Market & Customer",
    services: [
      { title: "Market & customer validation guidance", description: "Frameworks to test demand with real customers and assess competitive positioning before launch." },
    ],
    focus: "Validating market demand",
    challenge: "Limited validation of customer demand and competitive landscape",
  },
  no_startup_costs: {
    label: "Startup Costs",
    services: [
      { title: "Startup cost & break-even modelling", description: "Build a clear estimate of one-off setup costs and ongoing costs to identify the cash runway required to reach break-even." },
    ],
    focus: "Understanding the true cost of starting",
    challenge: "Startup costs and runway not fully mapped out",
  },
  startup_pricing: {
    label: "Pricing",
    services: [
      { title: "Pricing strategy framework", description: "Develop a pricing approach grounded in costs, value and market positioning rather than guesswork." },
    ],
    focus: "Confident pricing from day one",
    challenge: "Pricing approach not yet defined",
  },
  startup_funding_need: {
    label: "Startup Funding",
    services: [
      { title: "Startup funding readiness", description: "Prepare the package needed to approach lenders, grant programs or potential investors with confidence." },
      { title: "3-year financial forecast", description: "Build a credible P&L, cash flow and balance sheet forecast suitable for funding conversations." },
    ],
    focus: "Funding readiness for launch",
    challenge: "Upcoming need for external funding to launch",
  },
  no_startup_forecasts: {
    label: "Forecasts",
    services: [
      { title: "Startup financial forecast build", description: "Construct realistic 3-year financial forecasts including key assumptions and sensitivities." },
    ],
    focus: "Forecasting the first three years",
    challenge: "No financial forecasts in place for the first three years",
  },
  no_business_plan: {
    label: "Business Plan",
    services: [
      { title: "Startup business plan development", description: "Collaborative build of a working business plan covering concept, market, operations and financials." },
    ],
    focus: "A working business plan you'll actually use",
    challenge: "No written business plan to guide the launch",
  },
  setup_help: {
    label: "Setup & Structure",
    services: [
      { title: "Business structure & setup guidance", description: "Guidance on choosing the right structure, registrations (ABN, GST, licences) and day-one financial systems." },
    ],
    focus: "Setting up cleanly from day one",
    challenge: "Uncertainty around business structure, registrations or systems setup",
  },
  low_confidence_startup: {
    label: "Confidence",
    services: [
      { title: "Financial understanding sessions", description: "Personalised sessions to build confidence in reading financial reports and managing the numbers." },
    ],
    focus: "Owner financial confidence",
    challenge: "Low confidence with financial management",
  },
  startup_mentoring: {
    label: "Mentoring",
    services: [
      { title: "Launch & first-year mentoring", description: "Ongoing mentoring relationship with an EYB advisor through launch and the critical first 12 months." },
    ],
    focus: "Trusted advisor relationship",
    challenge: "Need for a trusted advisor through launch and the first year",
  },
};

export function getCategories(stage) {
  return stage === "pre_start" ? PRESTART_CATEGORIES : CATEGORIES;
}

export function analyzeAnswers(answers, stage = "existing") {
  const categories = getCategories(stage);
  const map = stage === "pre_start" ? PRESTART_SERVICE_MAP : SERVICE_MAP;

  const tagScores = {};

  categories.forEach((cat) => {
    cat.questions.forEach((q) => {
      const ans = answers[q.id];
      if (ans === undefined || ans === null || ans === "") return;

      if (q.type === "choice") {
        const opt = q.options.find((o) => o.value === ans);
        if (opt && opt.tags) {
          opt.tags.forEach((t) => {
            tagScores[t] = (tagScores[t] || 0) + (opt.weight || 1);
          });
        }
      } else if (q.type === "yesno") {
        if (ans === "yes" && q.yesTags) {
          q.yesTags.forEach((t) => {
            tagScores[t] = (tagScores[t] || 0) + (q.yesWeight || 1);
          });
        } else if (ans === "no" && q.noTags) {
          q.noTags.forEach((t) => {
            tagScores[t] = (tagScores[t] || 0) + (q.noWeight || 1);
          });
        }
      } else if (q.type === "slider") {
        const val = Number(ans);
        const max = q.max || 10;
        const normalized = q.scoreInverse ? (max - val) : val;
        if (q.tag && normalized > 0) {
          tagScores[q.tag] = (tagScores[q.tag] || 0) + normalized * (q.weight || 0.4);
        }
      }
    });
  });

  const triggered = Object.entries(tagScores)
    .filter(([, s]) => s >= 1)
    .sort((a, b) => b[1] - a[1])
    .map(([tag, score]) => ({ tag, score }));

  const seenTitles = new Set();
  const recommendedServices = [];
  const focusAreas = [];
  const challenges = [];

  triggered.forEach(({ tag }) => {
    const m = map[tag];
    if (!m) return;
    m.services.forEach((s) => {
      if (!seenTitles.has(s.title)) {
        seenTitles.add(s.title);
        recommendedServices.push({ ...s, tag });
      }
    });
    if (m.focus && !focusAreas.includes(m.focus)) focusAreas.push(m.focus);
    if (m.challenge && !challenges.includes(m.challenge)) challenges.push(m.challenge);
  });

  const totalScore = triggered.reduce((sum, t) => sum + t.score, 0);
  const denom = stage === "pre_start" ? 25 : 35;
  const riskScore = Math.min(100, Math.round((totalScore / denom) * 100));

  let riskLevel = "Low";
  let complexity = "Light";
  if (riskScore >= 65) { riskLevel = "Elevated"; complexity = "Comprehensive"; }
  else if (riskScore >= 35) { riskLevel = "Moderate"; complexity = "Moderate"; }

  const riskSummary = buildRiskSummary(challenges, riskLevel, stage);
  const engagementStyle = buildEngagementStyle(complexity, triggered.length, stage);

  return {
    triggered,
    tagScores,
    riskScore,
    riskLevel,
    complexity,
    recommendedServices,
    focusAreas,
    challenges,
    riskSummary,
    engagementStyle,
    priorityTags: triggered.slice(0, 3).map((t) => map[t.tag]?.label || t.tag),
  };
}

function buildRiskSummary(challenges, riskLevel, stage) {
  if (!challenges.length) {
    if (stage === "pre_start") {
      return "The pre-launch thinking is well progressed across most areas. A light-touch advisory engagement is likely sufficient to support a confident launch.";
    }
    return "The business appears to be operating with reasonable financial visibility. A light-touch advisory relationship is likely sufficient.";
  }
  const top = challenges.slice(0, 3).map((c) => c.toLowerCase()).join(", ");
  if (stage === "pre_start") {
    return `Readiness level is assessed as ${riskLevel.toLowerCase()}. The principal areas of attention before launch are ${top}. ` +
           "These are common pre-launch gaps and well-suited to a structured advisory engagement.";
  }
  return `Risk level is assessed as ${riskLevel.toLowerCase()}. The principal areas of attention are ${top}. ` +
         "These are common and addressable with a structured advisory engagement.";
}

function buildEngagementStyle(complexity, count, stage) {
  if (stage === "pre_start") {
    if (complexity === "Comprehensive") {
      return "We recommend a comprehensive pre-launch engagement covering concept, financials, plan and setup, " +
             "followed by ongoing mentoring through the first 12 months of trading.";
    }
    if (complexity === "Moderate") {
      return "A focused pre-launch engagement covering the priority areas, followed by quarterly mentoring sessions " +
             "during the critical first year of trading.";
    }
    return "A light-touch pre-launch engagement, beginning with a discovery session and clear deliverables, " +
           "with optional check-ins as the business gets under way.";
  }
  if (complexity === "Comprehensive") {
    return "We recommend a comprehensive 90-day advisory engagement followed by monthly ongoing support. " +
           "Sessions would combine structured workstreams (cash flow, profitability, systems) with one-to-one mentoring to embed change.";
  }
  if (complexity === "Moderate") {
    return "A focused 60-day engagement covering the priority areas, supported by quarterly check-ins thereafter. " +
           "The cadence is collaborative — regular but never overwhelming.";
  }
  return "A light-touch advisory engagement, beginning with a discovery session and quarterly reviews. " +
         "We work alongside you with clear deliverables and minimal disruption.";
}

export function buildInformationRequired(answers, client) {
  if (client.stage === "pre_start") {
    const items = [
      "Any draft business plan or concept notes",
      "Estimated startup cost workings (if any)",
      "Details of any conversations with potential customers or suppliers",
      "Preferred business structure (if known)",
      "Indicative timeline to launch",
    ];
    return items;
  }
  const items = new Set([
    "Most recent financial statements (P&L, balance sheet, cash flow)",
    "Year-to-date management accounts",
    "List of current debtors and creditors",
  ]);
  if (client.accounting_software && client.accounting_software !== "None / Not sure") {
    items.add(`Read-only access to ${client.accounting_software}`);
  } else {
    items.add("Current chart of accounts and bookkeeping records");
  }
  return Array.from(items);
}
